/**
 * WebContainerPreview — the "complex build" runtime viewer.
 *
 * Renders Claude-generated multi-file projects inside a browser-native
 * Node.js sandbox (StackBlitz WebContainers). Drop in `files` as a
 * FileSystemTree and the component does the rest:
 *   1. Boots WebContainer (cached singleton).
 *   2. Mounts the file tree.
 *   3. Installs dependencies (`npm install`).
 *   4. Starts the dev server (`npm run dev` or custom command).
 *   5. Hooks into `server-ready` and renders the live URL in an iframe.
 *
 * UX principles (Lovable/Bolt-grade)
 * ----------------------------------
 *   - Phase indicator at top (booting → installing → starting → ready)
 *   - Optional collapsible terminal showing real npm install / Vite output
 *   - Clean, dark, minimal — fits the Studio aesthetic
 *   - Graceful fallback when the browser is non-Chromium (we tell the user
 *     why, in one line)
 *   - Recoverable on error (Retry button calls teardown + re-mount)
 *
 * This component intentionally has no business logic beyond the runtime.
 * The decision "should we use iframe srcDoc or this viewer?" lives in the
 * parent (PreviewPanel.js).
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Loader2, Terminal, ChevronDown, ChevronUp, RotateCcw, Zap,
  CheckCircle2, AlertTriangle, ExternalLink,
} from 'lucide-react';
import { getContainer, isWebContainerSupported, tearDownContainer } from '../../lib/webcontainer';

const PHASES = {
  idle:       { label: 'Idle',                color: '#94A3B8' },
  booting:    { label: 'Booting runtime',     color: '#06B6D4' },
  mounting:   { label: 'Mounting files',      color: '#06B6D4' },
  installing: { label: 'Installing packages', color: '#A855F7' },
  starting:   { label: 'Starting dev server', color: '#FBBF24' },
  ready:      { label: 'Live',                color: '#22C55E' },
  error:      { label: 'Error',               color: '#FF6B6B' },
};

export default function WebContainerPreview({
  files,
  installCommand = 'npm install --legacy-peer-deps --no-audit --no-fund --loglevel=error',
  devCommand = 'npm run dev',
  height = '100%',
}) {
  const [phase, setPhase] = useState('idle');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);
  const [termOpen, setTermOpen] = useState(false);
  const iframeRef = useRef(null);
  const cleanupRef = useRef(null);
  const supported = isWebContainerSupported();

  const appendLog = useCallback((chunk) => {
    setLogs((prev) => {
      const next = [...prev, chunk];
      // Keep last ~200 chunks to avoid runaway memory
      return next.length > 200 ? next.slice(-200) : next;
    });
  }, []);

  const run = useCallback(async () => {
    if (!supported) {
      setPhase('error');
      setError('WebContainers require a Chromium-based browser (Chrome, Edge, Brave, Opera). Switch browser and reload to preview complex projects.');
      return;
    }
    if (!files || Object.keys(files).length === 0) {
      setPhase('error');
      setError('No files provided to the WebContainer preview.');
      return;
    }
    setError(null);
    setLogs([]);
    setPreviewUrl(null);

    try {
      // 1) Boot (singleton — instant on subsequent calls)
      setPhase('booting');
      const wc = await getContainer();

      // 2) Mount the file tree
      setPhase('mounting');
      await wc.mount(files);

      // 3) Subscribe to server-ready BEFORE spawning any process so we never miss it
      const offReady = wc.on('server-ready', (_port, url) => {
        setPreviewUrl(url);
        setPhase('ready');
      });
      const offErr = wc.on('error', (e) => {
        setError(e?.message || 'WebContainer runtime error');
        setPhase('error');
      });
      cleanupRef.current = () => { offReady(); offErr(); };

      // 4) Install deps
      setPhase('installing');
      const [installCmd, ...installArgs] = installCommand.split(/\s+/);
      const install = await wc.spawn(installCmd, installArgs);
      install.output.pipeTo(new WritableStream({ write: (chunk) => appendLog(chunk) }));
      const code = await install.exit;
      if (code !== 0) {
        throw new Error(`"${installCommand}" exited with code ${code}`);
      }

      // 5) Start dev server (don't await — it runs until teardown)
      setPhase('starting');
      const [devCmd, ...devArgs] = devCommand.split(/\s+/);
      const dev = await wc.spawn(devCmd, devArgs);
      dev.output.pipeTo(new WritableStream({ write: (chunk) => appendLog(chunk) }));
      // server-ready handler will flip the phase to 'ready' as soon as it fires.
    } catch (e) {
      console.error('[WebContainerPreview] run failed', e);
      setError(e?.message || String(e));
      setPhase('error');
    }
  }, [files, installCommand, devCommand, supported, appendLog]);

  // Run on mount and when files change. We deliberately compare by JSON shape
  // because FileSystemTree objects come from a parser and may be re-built on
  // each render; React would otherwise re-run on every parent re-render.
  const filesKey = JSON.stringify(files || {});
  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await run(); })();
    return () => {
      cancelled = true;
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesKey]);

  const handleRetry = useCallback(async () => {
    await tearDownContainer();
    await run();
  }, [run]);

  const meta = PHASES[phase] || PHASES.idle;
  const busy = phase !== 'ready' && phase !== 'error' && phase !== 'idle';

  return (
    <div
      data-testid="webcontainer-preview"
      style={{
        position: 'relative',
        width: '100%', height,
        background: '#0A0A0B', color: '#F5F5F7',
        display: 'flex', flexDirection: 'column',
        fontFamily: 'inherit',
        overflow: 'hidden',
      }}
    >
      {/* Status bar */}
      <div style={{
        flexShrink: 0,
        height: 38, padding: '0 14px',
        display: 'flex', alignItems: 'center', gap: 12,
        background: '#13131A',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <PhaseIndicator phase={phase} meta={meta} busy={busy} />
        <div style={{ flex: 1 }} />
        {previewUrl && (
          <a
            href={previewUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="wc-open-new-tab"
            style={iconBtn('rgba(255,255,255,0.55)')}
            title="Open in new tab"
          >
            <ExternalLink size={12} />
          </a>
        )}
        <button
          onClick={() => setTermOpen(v => !v)}
          data-testid="wc-toggle-terminal"
          style={iconBtn(termOpen ? '#22C55E' : 'rgba(255,255,255,0.55)')}
          title={termOpen ? 'Hide terminal' : 'Show terminal'}
        >
          <Terminal size={12} />
          {termOpen ? <ChevronDown size={11} /> : <ChevronUp size={11} />}
        </button>
        {(phase === 'error' || phase === 'ready') && (
          <button
            onClick={handleRetry}
            data-testid="wc-retry"
            style={iconBtn('rgba(255,255,255,0.55)')}
            title="Restart runtime"
          >
            <RotateCcw size={12} />
          </button>
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
        {phase === 'error' && <ErrorOverlay error={error} onRetry={handleRetry} />}
        {phase !== 'error' && !previewUrl && (
          <BootingOverlay phase={phase} meta={meta} />
        )}
        {previewUrl && (
          <iframe
            ref={iframeRef}
            src={previewUrl}
            data-testid="wc-iframe"
            title="WebContainer preview"
            style={{
              width: '100%', height: '100%',
              border: 'none',
              background: '#fff',
            }}
            sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups"
          />
        )}
      </div>

      {/* Terminal drawer */}
      {termOpen && (
        <div
          data-testid="wc-terminal"
          style={{
            flexShrink: 0,
            height: 200,
            background: '#000',
            color: '#E5E7EB',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            overflow: 'auto', padding: 10,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 11, lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
          }}
        >
          {logs.length === 0
            ? <span style={{ color: '#6B7280', fontStyle: 'italic' }}>Waiting for runtime output…</span>
            : logs.map((l, i) => <span key={i}>{l}</span>)}
        </div>
      )}
    </div>
  );
}

/* ----------------- subcomponents ----------------- */

function PhaseIndicator({ phase, meta, busy }) {
  const Icon = phase === 'ready' ? CheckCircle2
            : phase === 'error' ? AlertTriangle
            : busy ? Loader2 : Zap;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Icon
        size={13}
        color={meta.color}
        style={busy ? { animation: 'spin 0.9s linear infinite' } : undefined}
      />
      <span style={{
        fontSize: 10, fontWeight: 800, letterSpacing: '0.14em',
        textTransform: 'uppercase', color: meta.color,
      }}>
        {meta.label}
      </span>
      {phase === 'ready' && (
        <span style={{
          padding: '2px 7px', borderRadius: 999, fontSize: 9, fontWeight: 700,
          color: '#22C55E', background: 'rgba(34,197,94,0.12)',
          border: '1px solid rgba(34,197,94,0.35)',
          letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>WebContainer</span>
      )}
    </div>
  );
}

function BootingOverlay({ phase, meta }) {
  const steps = [
    { key: 'booting',    label: 'Boot Node.js runtime' },
    { key: 'mounting',   label: 'Mount project files' },
    { key: 'installing', label: 'Install dependencies' },
    { key: 'starting',   label: 'Start dev server' },
  ];
  const order = steps.findIndex(s => s.key === phase);
  return (
    <div style={{
      position: 'absolute', inset: 0,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 22, background: '#0A0A0B',
    }}>
      <Loader2 size={26} color={meta.color} style={{ animation: 'spin 0.9s linear infinite' }} />
      <div style={{
        fontSize: 11, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase',
        color: meta.color,
      }}>
        {meta.label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 240 }}>
        {steps.map((s, i) => {
          const done = i < order;
          const active = i === order;
          return (
            <div key={s.key} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 11.5,
              color: done ? '#22C55E' : active ? '#F5F5F7' : 'rgba(255,255,255,0.35)',
            }}>
              <span style={{
                width: 6, height: 6, borderRadius: '50%',
                background: done ? '#22C55E' : active ? meta.color : 'rgba(255,255,255,0.18)',
                boxShadow: active ? `0 0 12px ${meta.color}` : 'none',
              }} />
              {s.label}
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.04em', marginTop: 4 }}>
        First boot takes ~5–15 seconds. Subsequent runs are instant.
      </div>
    </div>
  );
}

function ErrorOverlay({ error, onRetry }) {
  return (
    <div style={{
      position: 'absolute', inset: 0,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 14, padding: 40, background: '#0A0A0B', textAlign: 'center',
    }}>
      <AlertTriangle size={28} color="#FF6B6B" />
      <div style={{ fontSize: 14, fontWeight: 800, color: '#F5F5F7', letterSpacing: '-0.005em' }}>
        Preview runtime failed
      </div>
      <div style={{
        fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, maxWidth: 520,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      }}>
        {error}
      </div>
      <button
        onClick={onRetry}
        data-testid="wc-error-retry"
        style={{
          padding: '8px 16px', fontSize: 11, fontWeight: 800, letterSpacing: '0.04em',
          background: '#FF6B6B', color: '#0A0A0B',
          border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
          display: 'inline-flex', alignItems: 'center', gap: 6,
          marginTop: 4,
        }}
      >
        <RotateCcw size={11} /> Retry
      </button>
    </div>
  );
}

function iconBtn(color) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '4px 8px',
    fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
    background: 'transparent', color,
    border: `1px solid ${color}33`,
    borderRadius: 6,
    cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'none',
  };
}
